import { pName } from "@/lib/format";

import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Check } from "lucide-react";
import { ScanButton, scanOnEnter } from "@/components/ProductCombobox";

export function MissedSaleDialog({ open, onOpenChange, branchId, source = "pos", prescriptionId = null, prefill = null, onLogged }) {
  const [mode, setMode] = useState("catalog");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [freeName, setFreeName] = useState("");
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setMode("catalog"); setQuery(""); setResults([]); setSelected(null); setFreeName(""); setQty(1); }
    else if (prefill) { setMode("catalog"); setSelected(prefill); }
  }, [open, prefill]);

  useEffect(() => {
    if (!open || mode !== "catalog") return;
    let active = true;
    const t = setTimeout(() => {
      api.get("/products", { params: { q: query || undefined } })
        .then((r) => { if (active) setResults((r.data || []).filter((p) => p.is_active).slice(0, 25)); })
        .catch(() => {});
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, mode, open]);

  const submit = async () => {
    if (!branchId) return toast.error("Select a branch first");
    const q = Math.max(1, Number(qty) || 1);
    const payload = { branch_id: branchId, quantity: q, source, prescription_id: prescriptionId };
    if (mode === "catalog") {
      if (!selected) return toast.error("Pick a medicine from the list");
      payload.product_id = selected.id;
      payload.product_name = pName(selected);
    } else {
      if (!freeName.trim()) return toast.error("Enter the medicine name");
      payload.product_name = freeName.trim();
    }
    setSaving(true);
    try {
      await api.post("/missed-sales", payload);
      toast.success("Missed sale recorded");
      onOpenChange(false);
      onLogged && onLogged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="missed-sale-dialog">
        <DialogHeader>
          <DialogTitle>Record missed sale</DialogTitle>
          <DialogDescription>Capture demand for a medicine you couldn't supply. This does not affect the current sale, stock or receipt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === "catalog" ? "default" : "outline"} data-testid="missed-mode-catalog" onClick={() => setMode("catalog")}>Out of stock (catalog)</Button>
            <Button type="button" size="sm" variant={mode === "freetext" ? "default" : "outline"} data-testid="missed-mode-freetext" onClick={() => setMode("freetext")}>Not carried (type name)</Button>
          </div>
          {mode === "catalog" ? (
            <div className="space-y-2">
              <div className="flex gap-1">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                <Input className="pl-9" placeholder="Search name, code or barcode — or scan…" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={scanOnEnter(results, setSelected)} data-testid="missed-search" />
              </div>
              <ScanButton products={results} onProduct={setSelected} testId="missed-scan" />
              </div>
              {selected && <div className="text-sm rounded-md bg-muted px-2.5 py-1.5" data-testid="missed-selected">Selected: <b>{pName(selected)}</b> · {selected.sku}</div>}
              <div className="max-h-48 overflow-y-auto divide-y divide-border border border-border rounded-md">
                {results.length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">No matches</div>}
                {results.map((p) => (
                  <button key={p.id} type="button" data-testid={`missed-option-${p.sku}`} onClick={() => setSelected(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                    <Check className={`h-4 w-4 shrink-0 ${selected?.id === p.id ? "opacity-100 text-emerald-600" : "opacity-0"}`} />
                    <span className="flex-1">{pName(p)}</span>
                    <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div><Label>Medicine name (not carried)</Label><Input value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder="e.g. Brand X 50mg tablets" data-testid="missed-freename" /></div>
          )}
          <div className="w-36"><Label>Quantity requested</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="missed-qty" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="missed-submit">{saving ? "Saving…" : "Record missed sale"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
